package com.vamvisions.main.components.background

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.vamvisions.main.core.domain.preferences.ColorScheme
import com.vamvisions.main.theme.AppStyleSheet
import com.vamvisions.main.theme.prefersReducedMotion
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Source
import org.jetbrains.compose.web.dom.Video
import org.w3c.dom.HTMLVideoElement
import org.w3c.dom.events.Event

private const val ASSET_PATH = "/background"

/**
 * The decorative, full-viewport background: the intro video plays once, muted, then the poster of its
 * last frame stays. The poster is always underneath, so ending, failing or blocked playback only
 * removes the video and nothing visibly jumps.
 *
 * Host it where it survives in-app navigation (the app shell), with the shell's [state]: it records there when
 * the intro has finished. The intro also ends for good when reduced motion is requested or the colour scheme
 * changes, so it never replays. Without motion, the video element is never created and no video is downloaded.
 */
@Composable
fun IntroBackground(colorScheme: ColorScheme, state: IntroState) {
    val reducedMotion by prefersReducedMotion()
    val introScheme = remember { colorScheme }
    val playIntro = !state.introFinished && !reducedMotion && colorScheme == introScheme
    LaunchedEffect(playIntro) {
        if (!playIntro) state.introFinished = true
    }

    val name = when (colorScheme) {
        ColorScheme.LIGHT -> "light"
        ColorScheme.DARK -> "dark"
    }
    val poster = "$ASSET_PATH/$name-poster.webp"
    Div(attrs = {
        classes(AppStyleSheet.introBackground)
        attr("aria-hidden", "true")
    }) {
        Img(src = poster, alt = "", attrs = { classes(AppStyleSheet.introBackgroundMedia) })
        if (playIntro) {
            IntroVideo(name, poster, onFinished = { state.introFinished = true })
        }
        Div(attrs = { classes(AppStyleSheet.introBackgroundOverlay) })
    }
}

@Composable
private fun IntroVideo(name: String, poster: String, onFinished: () -> Unit) {
    val finish: (Event) -> Unit = { onFinished() }
    Video(attrs = {
        classes(AppStyleSheet.introBackgroundMedia)
        attr("muted", "")
        attr("playsinline", "")
        attr("autoplay", "")
        attr("preload", "auto")
        attr("poster", poster)
        ref { element ->
            val video = element as HTMLVideoElement
            // The muted attribute set after creation does not mute the element, and autoplay needs it.
            video.muted = true
            video.addEventListener("ended", finish)
            video.addEventListener("error", finish)
            video.play().catch { onFinished() }
            onDispose {
                video.removeEventListener("ended", finish)
                video.removeEventListener("error", finish)
            }
        }
    }) {
        Source(attrs = {
            attr("src", "$ASSET_PATH/$name.webm")
            attr("type", "video/webm")
        })
        // When every source fails, the error is fired on the last source, not on the video.
        Source(attrs = {
            attr("src", "$ASSET_PATH/$name.mp4")
            attr("type", "video/mp4")
            ref { source ->
                source.addEventListener("error", finish)
                onDispose { source.removeEventListener("error", finish) }
            }
        })
    }
}
